import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { EVENT_TYPES, THEMES_BY_TYPE, PALETTES, surpriseMe } from "@/lib/eventOptions";
import RSVPFormBuilder from "@/components/RSVPFormBuilder";
import ImageUpload from "@/components/ImageUpload";
import BannerCropper from "@/components/BannerCropper";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, ArrowRight } from "lucide-react";

const STEPS = ["Type", "Theme", "Details", "RSVP", "Review"];

export default function CreateEvent() {
  const nav = useNavigate();
  const { id: editingId } = useParams();
  const isEdit = !!editingId;
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    event_type: "birthday",
    theme: "Elegant",
    custom_theme: "",
    palette: PALETTES[0].name,
    host_name: "",
    description: "",
    host_message: "",
    location_name: "",
    location_address: "",
    event_datetime: "",
    dress_code: "",
    rsvp_deadline: "",
    custom_questions: [],
    collect_guest_count: true,
    collect_dietary: true,
    allow_guest_invites: false,
    show_host_name: true,
    show_theme_motif: true,
    show_dress_code: true,
    show_description: true,
    show_host_message: true,
    show_countdown: true,
    motif_density: "minimal",
    banner_path: null,
    banner_position: "top",
    intro_label: "",
    hosted_by_label: "",
    countdown_label: "",
    when_label: "",
    where_label: "",
    attire_label: "",
    about_label: "",
    host_message_label: "",
    map_label: "",
    rsvp_intro_label: "",
    rsvp_title_label: "",
    custom_sections: [],
    enable_guestbook: false,
  });

  // Load existing event when editing
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/events/${editingId}`);
        // Convert ISO to datetime-local input value (YYYY-MM-DDTHH:mm)
        const dt = data.event_datetime ? data.event_datetime.slice(0, 16) : "";
        const deadline = data.rsvp_deadline ? data.rsvp_deadline.slice(0, 10) : "";
        setForm({
          title: data.title || "",
          event_type: data.event_type || "birthday",
          theme: data.theme || "Elegant",
          custom_theme: data.custom_theme || "",
          palette: data.palette || PALETTES[0].name,
          host_name: data.host_name || "",
          description: data.description || "",
          host_message: data.host_message || "",
          location_name: data.location_name || "",
          location_address: data.location_address || "",
          event_datetime: dt,
          dress_code: data.dress_code || "",
          rsvp_deadline: deadline,
          custom_questions: data.custom_questions || [],
          collect_guest_count: data.collect_guest_count !== false,
          collect_dietary: data.collect_dietary !== false,
          allow_guest_invites: !!data.allow_guest_invites,
          show_host_name: data.show_host_name !== false,
          show_theme_motif: data.show_theme_motif !== false,
          show_dress_code: data.show_dress_code !== false,
          show_description: data.show_description !== false,
          show_host_message: data.show_host_message !== false,
          show_countdown: data.show_countdown !== false,
          motif_density: data.motif_density || "minimal",
          banner_path: data.banner_path || null,
          banner_position: data.banner_position || "top",
          intro_label: data.intro_label || "",
          hosted_by_label: data.hosted_by_label || "",
          countdown_label: data.countdown_label || "",
          when_label: data.when_label || "",
          where_label: data.where_label || "",
          attire_label: data.attire_label || "",
          about_label: data.about_label || "",
          host_message_label: data.host_message_label || "",
          map_label: data.map_label || "",
          rsvp_intro_label: data.rsvp_intro_label || "",
          rsvp_title_label: data.rsvp_title_label || "",
          custom_sections: Array.isArray(data.custom_sections) ? data.custom_sections : [],
          enable_guestbook: !!data.enable_guestbook,
        });
      } catch (e) {
        toast.error(formatErr(e.response?.data?.detail) || "Could not load invite");
        nav("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, editingId, nav]);

  const themes = useMemo(() => THEMES_BY_TYPE[form.event_type] || THEMES_BY_TYPE.custom, [form.event_type]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const surprise = () => {
    const { theme, palette } = surpriseMe(form.event_type);
    setForm((f) => ({ ...f, theme, palette, custom_theme: "" }));
    toast.success(`Surprise — ${theme} in ${palette}`);
  };

  const canNext = () => {
    if (step === 0) return !!form.event_type;
    if (step === 1) return !!form.theme && !!form.palette && (form.theme !== "Other" || (form.custom_theme || "").trim().length > 0);
    if (step === 2) return form.title && form.host_name && form.location_name && form.location_address && form.event_datetime;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.rsvp_deadline) payload.rsvp_deadline = null;
      if (isEdit) {
        await api.put(`/events/${editingId}`, payload);
        toast.success("Invite updated");
        nav(`/events/${editingId}`);
      } else {
        const { data } = await api.post("/events", payload);
        toast.success("Invite created");
        nav(`/events/${data.id}`);
      }
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FBF9F4]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 text-neutral-500 text-sm">Loading your invite…</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FBF9F4]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Step {step + 1} of {STEPS.length}</div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl sm:text-5xl mt-2 font-light tracking-tight">
              {STEPS[step]}
            </h1>
          </div>
          <div className="hidden md:flex gap-2">
            {STEPS.map((s, i) => (
              <div key={s} className={`h-1 w-12 ${i <= step ? "bg-neutral-900" : "bg-neutral-200"}`} />
            ))}
          </div>
        </div>

        {step === 0 && (
          <div className="grid sm:grid-cols-3 gap-4">
            {EVENT_TYPES.map((t) => (
              <button
                key={t.value}
                onClick={() => set("event_type", t.value)}
                data-testid={`type-${t.value}`}
                className={`text-left border p-6 bg-white transition-all ${form.event_type === t.value ? "border-neutral-900 shadow-xl" : "border-black/10 hover:border-neutral-400"}`}
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Event</div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl mt-2 font-normal">{t.label}</div>
              </button>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Choose a theme</div>
              <Button onClick={surprise} variant="outline" data-testid="surprise-me" className="rounded-sm">
                <Sparkles size={14} className="mr-2" /> Surprise Me
              </Button>
            </div>
            <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {themes.map((t) => (
                <button
                  key={t}
                  onClick={() => set("theme", t)}
                  data-testid={`theme-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`border p-5 bg-white text-left transition-all ${form.theme === t ? "border-neutral-900 shadow-lg" : "border-black/10 hover:border-neutral-400"}`}
                >
                  <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-xl">{t}</div>
                </button>
              ))}
            </div>
            {form.theme === "Other" && (
              <div>
                <Label className="text-[10px] tracking-[0.25em] uppercase">Describe your theme</Label>
                <Input data-testid="custom-theme" value={form.custom_theme} onChange={(e) => set("custom_theme", e.target.value)} placeholder="e.g. Studio Ghibli forest" className="rounded-sm mt-1" />
              </div>
            )}

            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Colour palette</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {PALETTES.map((p) => (
                  <button
                    key={p.name}
                    onClick={() => set("palette", p.name)}
                    data-testid={`palette-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`border p-4 text-left transition-all ${form.palette === p.name ? "border-neutral-900 shadow-lg" : "border-black/10 hover:border-neutral-400"}`}
                    style={{ background: p.bg }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="h-5 w-5 rounded-full border border-black/10" style={{ background: p.primary }} />
                      <span className="h-5 w-5 rounded-full border border-black/10" style={{ background: p.accent }} />
                      <span className="h-5 w-5 rounded-full border border-black/10" style={{ background: p.bg }} />
                    </div>
                    <div className="text-sm font-medium" style={{ color: p.primary }}>{p.name}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Decoration</div>
              <p className="text-xs text-neutral-500 mb-3">Minimal keeps the invite quiet and elegant. Illustrated frames the page with colourful cartoon artwork matched to your theme — above and below the text, never on top of it.</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => set("motif_density", "minimal")}
                  data-testid="density-minimal"
                  className={`border p-5 bg-white text-left transition-all ${form.motif_density === "minimal" ? "border-neutral-900 shadow-lg" : "border-black/10 hover:border-neutral-400"}`}
                >
                  <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-xl">Minimal</div>
                  <p className="text-xs text-neutral-500 mt-1">Clean, typographic, almost nothing in the way.</p>
                </button>
                <button
                  type="button"
                  onClick={() => set("motif_density", "busy")}
                  data-testid="density-busy"
                  className={`border p-5 bg-white text-left transition-all ${form.motif_density === "busy" ? "border-neutral-900 shadow-lg" : "border-black/10 hover:border-neutral-400"}`}
                >
                  <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-xl">Illustrated</div>
                  <p className="text-xs text-neutral-500 mt-1">A cartoon-style top and bottom band matched to your theme.</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase">Title of the invite</Label>
              <Input data-testid="field-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Maya's 30th Birthday" className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase">Hosted by</Label>
              <Input data-testid="field-host" value={form.host_name} onChange={(e) => set("host_name", e.target.value)} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase">Date & time</Label>
              <Input data-testid="field-datetime" type="datetime-local" value={form.event_datetime} onChange={(e) => set("event_datetime", e.target.value)} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase">Venue name</Label>
              <Input data-testid="field-location-name" value={form.location_name} onChange={(e) => set("location_name", e.target.value)} placeholder="The Rooftop" className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase">Address (for map)</Label>
              <Input data-testid="field-location-address" value={form.location_address} onChange={(e) => set("location_address", e.target.value)} placeholder="123 Main St, City" className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase">Dress code</Label>
              <Input data-testid="field-dress" value={form.dress_code} onChange={(e) => set("dress_code", e.target.value)} placeholder="Cocktail attire" className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase">RSVP by (optional)</Label>
              <Input data-testid="field-rsvp-deadline" type="date" value={form.rsvp_deadline} onChange={(e) => set("rsvp_deadline", e.target.value)} className="rounded-sm mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase">Description</Label>
              <Textarea data-testid="field-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="A short note about the event" className="rounded-sm mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase">Banner photo (optional)</Label>
              <p className="text-xs text-neutral-500 mt-1 mb-2">Shown on the invite. Keep it light — theme motifs do the decorating.</p>
              <ImageUpload
                value={form.banner_path}
                onChange={(path) => set("banner_path", path)}
                label="Upload a banner photo"
                dataTestId="banner-upload"
              />
              {form.banner_path && (
                <button
                  type="button"
                  data-testid="banner-crop-open"
                  onClick={() => setCropperOpen(true)}
                  className="mt-3 text-xs tracking-[0.2em] uppercase border border-neutral-900 px-3 py-2 hover:bg-neutral-900 hover:text-white transition-colors"
                >
                  Reposition / crop photo
                </button>
              )}
              {form.banner_path && (
                <div className="mt-4">
                  <Label className="text-[10px] tracking-[0.25em] uppercase">Photo position</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button
                      type="button"
                      data-testid="banner-pos-top"
                      onClick={() => set("banner_position", "top")}
                      className={`border p-3 text-left text-xs ${form.banner_position === "top" ? "border-neutral-900 shadow" : "border-black/10 hover:border-neutral-400"}`}
                    >
                      <div className="font-medium">At the very top</div>
                      <div className="text-neutral-500">Before the illustrated band.</div>
                    </button>
                    <button
                      type="button"
                      data-testid="banner-pos-after"
                      onClick={() => set("banner_position", "after_band")}
                      className={`border p-3 text-left text-xs ${form.banner_position === "after_band" ? "border-neutral-900 shadow" : "border-black/10 hover:border-neutral-400"}`}
                    >
                      <div className="font-medium">Below the illustrated band</div>
                      <div className="text-neutral-500">Art first, then your photo.</div>
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase">Message to your guests</Label>
              <Textarea data-testid="field-host-message" rows={3} value={form.host_message} onChange={(e) => set("host_message", e.target.value)} placeholder="A personal word from the host" className="rounded-sm mt-1" />
            </div>
            <div className="sm:col-span-2 border border-black/10 bg-white p-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Wording on the invite</div>
              <p className="text-xs text-neutral-500 mb-4">Every field is optional — leave blank to use the beautiful defaults. Rewrite any of them to make the invite sound like you.</p>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="sm:col-span-2">
                  <Label className="text-[10px] tracking-[0.25em] uppercase">Top line (above the title)</Label>
                  <Input data-testid="label-intro" value={form.intro_label} onChange={(e) => set("intro_label", e.target.value)} placeholder={`You're invited to a ${form.event_type.replace("_", " ")}`} className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.25em] uppercase">&ldquo;Hosted by&rdquo; label</Label>
                  <Input data-testid="label-hostedby" value={form.hosted_by_label} onChange={(e) => set("hosted_by_label", e.target.value)} placeholder="Hosted by" className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.25em] uppercase">Countdown heading</Label>
                  <Input data-testid="label-countdown" value={form.countdown_label} onChange={(e) => set("countdown_label", e.target.value)} placeholder="The moment approaches" className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.25em] uppercase">&ldquo;When&rdquo; label</Label>
                  <Input data-testid="label-when" value={form.when_label} onChange={(e) => set("when_label", e.target.value)} placeholder="When" className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.25em] uppercase">&ldquo;Where&rdquo; label</Label>
                  <Input data-testid="label-where" value={form.where_label} onChange={(e) => set("where_label", e.target.value)} placeholder="Where" className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.25em] uppercase">&ldquo;Attire&rdquo; label</Label>
                  <Input data-testid="label-attire" value={form.attire_label} onChange={(e) => set("attire_label", e.target.value)} placeholder="Attire" className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.25em] uppercase">&ldquo;About&rdquo; label</Label>
                  <Input data-testid="label-about" value={form.about_label} onChange={(e) => set("about_label", e.target.value)} placeholder="About" className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.25em] uppercase">Host message heading</Label>
                  <Input data-testid="label-hostmsg" value={form.host_message_label} onChange={(e) => set("host_message_label", e.target.value)} placeholder="A word from your host" className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.25em] uppercase">Map heading</Label>
                  <Input data-testid="label-map" value={form.map_label} onChange={(e) => set("map_label", e.target.value)} placeholder="Find your way" className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.25em] uppercase">RSVP intro line</Label>
                  <Input data-testid="label-rsvpintro" value={form.rsvp_intro_label} onChange={(e) => set("rsvp_intro_label", e.target.value)} placeholder="Kindly respond" className="rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.25em] uppercase">RSVP heading</Label>
                  <Input data-testid="label-rsvptitle" value={form.rsvp_title_label} onChange={(e) => set("rsvp_title_label", e.target.value)} placeholder="RSVP" className="rounded-sm mt-1" />
                </div>
              </div>
            </div>
            <div className="sm:col-span-2 border border-black/10 bg-white p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Additional sections</div>
                  <p className="text-xs text-neutral-500 mt-1">Add your own info blocks to the invite — parking, gifts, schedule, anything you like. They appear after the main details.</p>
                </div>
                <Button
                  type="button"
                  data-testid="add-section"
                  variant="outline"
                  size="sm"
                  onClick={() => set("custom_sections", [...form.custom_sections, { title: "", body: "" }])}
                  className="rounded-sm"
                >
                  + Add a section
                </Button>
              </div>
              {form.custom_sections.length === 0 ? (
                <div className="text-xs text-neutral-400 italic">No extra sections yet.</div>
              ) : (
                <div className="space-y-4">
                  {form.custom_sections.map((s, i) => (
                    <div key={i} data-testid={`section-row-${i}`} className="border border-black/10 p-4 bg-[#FBF9F4]/50">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 grid sm:grid-cols-3 gap-3">
                          <div className="sm:col-span-1">
                            <Label className="text-[10px] tracking-[0.25em] uppercase">Section title</Label>
                            <Input
                              data-testid={`section-title-${i}`}
                              value={s.title}
                              onChange={(e) => {
                                const next = [...form.custom_sections];
                                next[i] = { ...next[i], title: e.target.value };
                                set("custom_sections", next);
                              }}
                              placeholder="e.g. Parking, Gifts, Schedule"
                              className="rounded-sm mt-1"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <Label className="text-[10px] tracking-[0.25em] uppercase">Body</Label>
                            <Textarea
                              data-testid={`section-body-${i}`}
                              rows={3}
                              value={s.body}
                              onChange={(e) => {
                                const next = [...form.custom_sections];
                                next[i] = { ...next[i], body: e.target.value };
                                set("custom_sections", next);
                              }}
                              placeholder="What guests need to know"
                              className="rounded-sm mt-1"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          data-testid={`section-remove-${i}`}
                          onClick={() => set("custom_sections", form.custom_sections.filter((_, j) => j !== i))}
                          className="text-neutral-400 hover:text-red-600 mt-6"
                          title="Remove section"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="sm:col-span-2 border border-black/10 bg-white p-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Display on the invite</div>
              <p className="text-xs text-neutral-500 mb-4">Untick anything you don&apos;t want shown. The invite adapts automatically.</p>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <label className="flex items-center gap-2"><Checkbox data-testid="show-host-name" checked={form.show_host_name} onCheckedChange={(v) => set("show_host_name", !!v)} /> Show &ldquo;Hosted by&rdquo;</label>
                <label className="flex items-center gap-2"><Checkbox data-testid="show-countdown" checked={form.show_countdown} onCheckedChange={(v) => set("show_countdown", !!v)} /> Show countdown</label>
                <label className="flex items-center gap-2"><Checkbox data-testid="show-description" checked={form.show_description} onCheckedChange={(v) => set("show_description", !!v)} /> Show description</label>
                <label className="flex items-center gap-2"><Checkbox data-testid="show-dress-code" checked={form.show_dress_code} onCheckedChange={(v) => set("show_dress_code", !!v)} /> Show dress code</label>
                <label className="flex items-center gap-2"><Checkbox data-testid="show-host-message" checked={form.show_host_message} onCheckedChange={(v) => set("show_host_message", !!v)} /> Show host message</label>
                <label className="flex items-center gap-2"><Checkbox data-testid="show-theme-motif" checked={form.show_theme_motif} onCheckedChange={(v) => set("show_theme_motif", !!v)} /> Show theme ornaments</label>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div className="flex items-center gap-6 border border-black/10 bg-white p-5 flex-wrap">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.collect_guest_count} onCheckedChange={(v) => set("collect_guest_count", !!v)} data-testid="toggle-guest-count" />
                Ask number of guests
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.collect_dietary} onCheckedChange={(v) => set("collect_dietary", !!v)} data-testid="toggle-dietary" />
                Ask dietary restrictions
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.allow_guest_invites} onCheckedChange={(v) => set("allow_guest_invites", !!v)} data-testid="toggle-guest-invites" />
                Let guests invite their friends (+1s)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.enable_guestbook} onCheckedChange={(v) => set("enable_guestbook", !!v)} data-testid="toggle-guestbook" />
                Enable guestbook / memory wall
              </label>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">Custom questions</div>
              <RSVPFormBuilder questions={form.custom_questions} setQuestions={(q) => set("custom_questions", q)} />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="border border-black/10 bg-white p-8 space-y-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Review</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl font-light">{form.title || "Untitled"}</h2>
            <div className="text-sm text-neutral-600">Hosted by {form.host_name}</div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><span className="text-neutral-500">Type:</span> {form.event_type}</div>
              <div><span className="text-neutral-500">Theme:</span> {form.theme === "Other" ? form.custom_theme : form.theme}</div>
              <div><span className="text-neutral-500">Palette:</span> {form.palette}</div>
              <div><span className="text-neutral-500">When:</span> {form.event_datetime}</div>
              <div className="sm:col-span-2"><span className="text-neutral-500">Where:</span> {form.location_name} — {form.location_address}</div>
            </div>
            <div className="text-sm"><span className="text-neutral-500">Custom questions:</span> {form.custom_questions.length}</div>
          </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="step-back" className="rounded-sm">
            <ArrowLeft size={16} className="mr-2" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => canNext() ? setStep(step + 1) : toast.error("Please fill required fields")} data-testid="step-next" className="rounded-sm bg-neutral-900 hover:bg-neutral-800">
              Next <ArrowRight size={16} className="ml-2" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={saving} data-testid="create-submit" className="rounded-sm bg-neutral-900 hover:bg-neutral-800">
              {saving ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save changes" : "Create Invite")}
            </Button>
          )}
        </div>
      </div>
      <BannerCropper
        open={cropperOpen}
        onOpenChange={setCropperOpen}
        sourcePath={form.banner_path}
        onCropped={(path) => set("banner_path", path)}
      />
    </div>
  );
}
